import { api } from '../redux/api';

/**
 * Utility functions for checking version conflicts across multiple templates
 */

const SCREEN_NAME = {
  inbound: "inbound_templates/",
  outbound: "outbound_templates/",
  workflow: "",
};

/**
 * Generate template path for revision history check
 */
export const getTemplatePath = (account, workflowGroup, templateType, templateName) => {
  const screenType = templateType === 'inbound' ? 'inbound' : 
                    templateType === 'outbound' ? 'outbound' : 'workflow';
  return `${account}/${workflowGroup}/${SCREEN_NAME[screenType]}${templateName}`;
};

/**
 * Get template header revision ID for userspace comparison
 */
const getTemplateHeaderRevisionId = (templateType, treeData) => {
  try {
    const templateHeader = treeData?.[templateType]?.rightTree?.tree?.templateHeader;
    return parseInt(templateHeader?.REVISION_ID) || 0;
  } catch (error) {
    console.warn('Could not get template header revision ID:', error);
    return 0;
  }
};

/**
 * Check version conflicts for a single template
 * Uses template header REVISION_ID as userspace revision and API revisionId as server revision
 */
export const checkSingleTemplateVersionConflict = async (dispatch, template, account, treeData = null) => {
  try {
    const path = getTemplatePath(
      account, 
      template.workflow_group, 
      template.template_type, 
      template.template
    );
    
    const response = await dispatch(api.endpoints.getServerRevisionhistory.initiate({
      account,
      path,
    }, {forceRefetch: true}));

    if (response.data && response.data.data.length > 0) {
      const latestRevision = response.data.data[0];
      
      // Get userspace revision from template header
      let userspaceRevision = 0;
      if (treeData) {
        userspaceRevision = getTemplateHeaderRevisionId(template.template_type, treeData);
      }
      
      // Server revision from API
      const serverRevision = parseInt(latestRevision.revisionId === "null" ? "0" : latestRevision.revisionId) || 0;
      
      // Check if server has newer version than userspace template
      if (userspaceRevision < serverRevision) {
        return {
          title: template.template,
          workflowGroup: template.workflow_group,
          type: template.template_type,
          userspace: { localRevisionId: userspaceRevision },
          server: { revisionId: serverRevision },
          error: `Server has newer version (revision ${serverRevision}) than template (revision ${userspaceRevision})`,
          templateData: template // Store original template data for processing
        };
      }
    }
    
    return null; 
  } catch (error) {
    console.error('Error checking version conflicts for template:', template.template, error);
    return null;
  }
};

/**
 * Check version conflicts for multiple templates
 */
export const checkMultipleTemplatesVersionConflicts = async (dispatch, templates, account, treeData = null) => {
  const conflicts = [];
  
  // Flatten all template types into a single array for processing
  const allTemplates = [];
  
  if (templates.inbound_templates) {
    templates.inbound_templates.forEach(template => {
      allTemplates.push({
        ...template,
        template_type: 'inbound'
      });
    });
  }
  
  if (templates.outbound_templates) {
    templates.outbound_templates.forEach(template => {
      allTemplates.push({
        ...template,
        template_type: 'outbound'
      });
    });
  }
  
  if (templates.workflow) {
    templates.workflow.forEach(template => {
      allTemplates.push({
        ...template,
        template_type: 'workflow'
      });
    });
  }
  
  // Check each template for version conflicts
  for (const template of allTemplates) {
    const conflict = await checkSingleTemplateVersionConflict(dispatch, template, account, treeData);
    if (conflict) {
      conflicts.push(conflict);
    }
  }
  
  return conflicts;
};

/**
 * Filter templates to remove conflicted ones based on user decisions
 */
export const filterTemplatesAfterConflictResolution = (originalTemplates, conflictsToSkip) => {
  const filtered = {
    inbound_templates: [],
    outbound_templates: [],
    workflow: []
  };
  
  // Helper function to check if a template should be skipped
  const shouldSkipTemplate = (template, templateType) => {
    return conflictsToSkip.some(conflict => 
      conflict.templateData.template === template.template &&
      conflict.templateData.workflow_group === template.workflow_group &&
      conflict.templateData.template_type === templateType
    );
  };
  
  // Filter inbound templates
  if (originalTemplates.inbound_templates) {
    filtered.inbound_templates = originalTemplates.inbound_templates.filter(
      template => !shouldSkipTemplate(template, 'inbound')
    );
  }
  
  // Filter outbound templates
  if (originalTemplates.outbound_templates) {
    filtered.outbound_templates = originalTemplates.outbound_templates.filter(
      template => !shouldSkipTemplate(template, 'outbound')
    );
  }
  
  // Filter workflow templates
  if (originalTemplates.workflow) {
    filtered.workflow = originalTemplates.workflow.filter(
      template => !shouldSkipTemplate(template, 'workflow')
    );
  }
  
  return filtered;
};
